import React, { useState, useEffect, useContext } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import DraggableEvent from './DraggableEvent';
import EventDrawer from './EventDrawer';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { CheckCircleOutline } from '@mui/icons-material';
import { GlobalSettingsContext } from '../../firebase/GlobalSettingsContext';

function SnakeEventManager({ initialEvents, presentEvent, setOuterEvents, competition }) {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [newEvent, setNewEvent] = useState({ type: 'Event', name: '', duration: '' });
    const { firebase } = useContext(FirebaseNewContext);
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const { globalSettings } = useContext(GlobalSettingsContext);

    const columns = 4;

    // Function to compute the snake-adjusted index for a given index and column count.
    const adjustedIndex = (index, columns) => {
        const row = Math.floor(index / columns);
        const positionInRow = index % columns;
        return row % 2 === 0 
            ? index
            : (row + 1) * columns - positionInRow - 1;
    };

    // Normalize events when they load:
    // Convert legacy object shapes and ensure each event has a stable id and originalIndex (playback index)
    useEffect(() => {
        if (initialEvents) {
            // Accept either array or object from DB
            const list = Array.isArray(initialEvents)
                ? initialEvents
                : (typeof initialEvents === 'object' ? Object.keys(initialEvents).sort((a,b)=>Number(a)-Number(b)).map(k=>initialEvents[k]) : []);

            if (list.length > 0) {
                const now = Date.now();
                const normalized = list.map((e, i) => ({
                    ...e,
                    id: e && (e.id || e.uid || e.originalIndex) != null ? (e.id || e.uid || e.originalIndex) : `ev-${now}-${i}`,
                    // originalIndex records initial playback index on load
                    originalIndex: i,
                }));
                setEvents(normalized);
            } else {
                setEvents([]);
            }
        } else {
            setEvents([]);
        }
        setSelectedCompetition(competition);
    }, [initialEvents, competition]);

    const handleEventClick = (event, playbackIndex) => {
        let { type, name, duration, carousel, image } = event;
        switch (type) {
            case 'Event':
                name = name || 'Event';
                duration = duration || 0;
                break;
            case 'Break':
                name = name || 'Break';
                duration = duration || 0;
                break;
            case 'Lunch':
                name = name || 'Lunch';
                duration = duration || 0;
                break;
            default:
                type = 'Custom';
                break;
        }
        // store playbackIndex so drawer actions operate on array index
        setSelectedEvent({ ...event, playbackIndex });
        setNewEvent({ type, name, duration, carousel, image });
        setDrawerOpen(true);
    };

    const handleAddClick = () => {
        setNewEvent({ type: 'Event', name: 'Event', duration: '', carousel: false, image: 'default' });
        setDrawerOpen(true);
    };

    const handleTypeChange = (e) => {
        const type = e.target.value;
        let name = '';
        let duration = '';
        let carousel = false;
        switch (type) {
            case 'Event':
                name = 'Event';
                duration = 15;
                break;
            case 'Break':
                name = 'Break';
                duration = 30;
                carousel = true;
                break;
            case 'Lunch':
                name = 'Lunch';
                duration = 45;
                carousel = true;
                break;
            case 'Custom':
                name = '';
                duration = '';
                break;
            default:
                break;
        }
        setNewEvent({ ...newEvent, type, name, duration, carousel });
    };

    // When saving an edited or new event, operate on playback index (array index)
    const handleSave = () => {
        let updatedEvents = [...events];
        if (selectedEvent) {
            const idx = typeof selectedEvent.playbackIndex === 'number'
                ? selectedEvent.playbackIndex
                : events.findIndex(e => e.id === selectedEvent.id || e.originalIndex === selectedEvent.originalIndex);
            if (idx !== -1) {
                updatedEvents[idx] = {
                    ...updatedEvents[idx],
                    ...newEvent,
                    duration: newEvent.duration === '' ? 0 : newEvent.duration,
                    id: updatedEvents[idx].id || `ev-${Date.now()}`,
                };
                setEvents(updatedEvents);
                setOuterEvents(updatedEvents);
            }
        } else if (newEvent) {
            const id = `ev-${Date.now()}`;
            const newEventNormalized = {
                ...newEvent,
                id,
                duration: newEvent.duration === '' ? 0 : newEvent.duration,
            };
            updatedEvents = [...events, newEventNormalized];
            setEvents(updatedEvents);
            setOuterEvents(updatedEvents);
        }

        setNewEvent({ type: 'Event', name: '', duration: '' });
        setSelectedEvent(null);
        setDrawerOpen(false);
    };

    const deleteEvent = (playbackIndex) => {
        let idx = playbackIndex;
        if (typeof idx !== 'number') {
            idx = events.findIndex(ev => ev.id === playbackIndex || ev.originalIndex === playbackIndex);
        }
        if (idx === -1) {
            setSelectedEvent(null);
            setDrawerOpen(false);
            return;
        }
        const updatedEvents = events.filter((_, i) => i !== idx);
        setEvents(updatedEvents);
        setOuterEvents(updatedEvents);
        setSelectedEvent(null);
        setDrawerOpen(false);
    };

    // Simple moveEvent that operates on playback indices (array indices)
    const moveEvent = (fromIndex, toIndex, initialEventsList) => {
        const list = initialEventsList !== undefined ? initialEventsList : events;
        if (fromIndex === toIndex) return list;
        const copy = [...list];
        // guard indices
        const from = Math.max(0, Math.min(copy.length - 1, fromIndex));
        const to = Math.max(0, Math.min(copy.length - 1, toIndex));
        try {
            // eslint-disable-next-line no-console
            console.log('[moveEvent] fromIndex', fromIndex, 'toIndex', toIndex, 'clampedFrom', from, 'clampedTo', to, 'idsBefore', copy.map(i=>i.id || i.originalIndex));
        } catch (e) {}
        const [item] = copy.splice(from, 1);
        copy.splice(to, 0, item);
        try {
            // eslint-disable-next-line no-console
            console.log('[moveEvent] idsAfter', copy.map(i=>i.id || i.originalIndex));
        } catch (e) {}
        setEvents(copy);
        setOuterEvents(copy);
        return copy;
    };

    const renderEvents = () => {
        const rows = Math.ceil(events.length / columns);
        const totalSlots = rows * columns;
        // Build displaySlots mapping based on snake ordering
        const displaySlots = Array(totalSlots).fill(null);

        // Place each event (playback index p) into the correct display slot
        events.forEach((event, p) => {
            const row = Math.floor(p / columns);
            const posInRow = p % columns;
            let displayPos;
            if (row % 2 === 0) {
                displayPos = row * columns + posInRow; // left-to-right
            } else {
                displayPos = row * columns + (columns - 1 - posInRow); // right-to-left
            }
            displaySlots[displayPos] = { event, p };
        });

        const rendered = [];
        for (let row = 0; row < rows; row++) {
            const start = row * columns;
            const rowSlots = displaySlots.slice(start, start + columns);
            for (let col = 0; col < rowSlots.length; col++) {
                const slot = rowSlots[col];
                const displayIndex = start + col;
                    if (slot && slot.event) {
                    const { event, p } = slot;
                    rendered.push(
                        <DraggableEvent
                            key={event.id != null ? event.id : displayIndex}
                            event={event}
                            index={p}
                            columns={columns}
                            moveEvent={moveEvent}
                            handleEventClick={() => handleEventClick(event, p)}
                            renderArrow={() => renderArrow(p)}
                            disabled={globalSettings.lockCompetition != undefined && globalSettings.lockCompetition[selectedCompetition]}
                        />
                    );
                } else {
                    rendered.push(<div key={`empty-${displayIndex}`} />);
                }
            }
        }

        return rendered;
    };

    const displayPosOf = (p) => {
        const row = Math.floor(p / columns);
        const posInRow = p % columns;
        return row % 2 === 0 ? row * columns + posInRow : row * columns + (columns - 1 - posInRow);
    };

    const renderArrow = (p) => {
        if (p === events.length - 1) return <CheckCircleOutline />;
        const pos = displayPosOf(p);
        const nextPos = displayPosOf(p + 1);
        // same row, next column to the right
        if (nextPos === pos + 1) return <ArrowForwardIcon />;
        // same row, next column to the left
        if (nextPos === pos - 1) return <ArrowBackIcon />;
        return <ArrowDownwardIcon />;
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                        gridAutoRows: 'minmax(100px, auto)',
                        gap: '8px',
                    }}
                >
                    {renderEvents()}
                    <Paper
                        onClick={handleAddClick}
                        style={{
                            padding: '16px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <AddIcon style={{ fontSize: 48 }} />
                    </Paper>
                </div>
                <EventDrawer
                    drawerOpen={drawerOpen}
                    selectedEvent={selectedEvent}
                    setSelectedEvent={setSelectedEvent}
                    newEvent={newEvent}
                    setNewEvent={setNewEvent}
                    handleTypeChange={handleTypeChange}
                    handleSave={handleSave}
                    setDrawerOpen={setDrawerOpen}
                    deleteEvent={deleteEvent}
                    presentEvent={presentEvent}
                    firebase={firebase}
                    selectedCompetition={selectedCompetition}
                />
            </div>
        </DndProvider>
    );
}

export default SnakeEventManager;
